/**
 * Disable Organisation Page
 *
 * System Settings screen (system owner only) for permanently disabling an
 * organisation that was created by mistake or is fake/spam. It loads the org
 * and surfaces its identity + impact before requiring an explicit, ceremonial
 * confirmation. The disable action reuses the existing DELETE org endpoint.
 */

import React, { useState, useCallback } from 'react';
import {
  Button,
  Form,
  Input,
  Loader,
  Message,
  Segment
} from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { loadOrganization, disableOrganization } from './api';
import { parseOrgId } from './helpers';
import { OrgLoadResult } from './types';
import { i18nKeys } from './constants';
import OrgDetailsCard from './components/OrgDetailsCard';
import OrgAdminsList from './components/OrgAdminsList';
import DisableConfirmationModal from './components/DisableConfirmationModal';
import styles from './DisableOrganization.module.css';

const DisableOrganization: React.FC = () => {
  const [orgIdInput, setOrgIdInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [data, setData] = useState<OrgLoadResult | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);

  const resetToStart = useCallback(() => {
    setOrgIdInput('');
    setData(null);
    setLoadError(null);
    setValidationError(null);
    setModalOpen(false);
    setDisableError(null);
  }, []);

  const handleLoad = useCallback(async () => {
    const orgId = parseOrgId(orgIdInput);
    if (orgId === null) {
      // Clear any previously loaded org so stale details are not shown next
      // to the validation error. A bad id is a client-side validation problem,
      // not a backend failure, so it is surfaced separately from loadError.
      setData(null);
      setModalOpen(false);
      setDisableError(null);
      setSuccessMessage(null);
      setLoadError(null);
      setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_INVALID_ID));
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    setValidationError(null);
    setSuccessMessage(null);
    setData(null);

    try {
      const result = await loadOrganization(orgId);
      setData(result);
    } catch (err: any) {
      setLoadError(err?.message || I18n.t(i18nKeys.ERRORS.LOAD_FAILED));
    } finally {
      setIsLoading(false);
    }
  }, [orgIdInput]);

  const handleConfirmDisable = useCallback(async () => {
    if (!data) {
      return;
    }
    setIsDisabling(true);
    setDisableError(null);

    try {
      await disableOrganization(data.details.id);
      const disabledName = data.details.name;
      resetToStart();
      setSuccessMessage(I18n.t(i18nKeys.SUCCESS, { name: disabledName }));
    } catch (err: any) {
      setDisableError(err?.message || I18n.t(i18nKeys.ERRORS.DISABLE_FAILED));
    } finally {
      setIsDisabling(false);
    }
  }, [data, resetToStart]);

  return (
    <div className={styles.container}>
      <h2>
        <Translate value={i18nKeys.TITLE} />
      </h2>

      {/* Explanation / note */}
      <Segment className={styles.explanationSegment}>
        <h4>
          <Translate value={i18nKeys.EXPLANATION.TITLE} />
        </h4>
        <div className={styles.explanationContent}>
          <p>
            <Translate value={i18nKeys.EXPLANATION.BODY} />
          </p>
          <p>
            <Translate value={i18nKeys.EXPLANATION.USE_FOR} />
          </p>
          <p>
            <Translate value={i18nKeys.EXPLANATION.DO_NOT_USE} />
          </p>
          <p>
            <strong>
              <Translate value={i18nKeys.EXPLANATION.IRREVERSIBLE} />
            </strong>
          </p>
        </div>
      </Segment>

      {/* Success message */}
      {successMessage && (
        <Message positive onDismiss={() => setSuccessMessage(null)}>
          <p>{successMessage}</p>
        </Message>
      )}

      {/* Org id input */}
      <Segment className={styles.formSegment}>
        <Form
          onSubmit={() => {
            handleLoad();
          }}
        >
          <Form.Field>
            <label>
              <Translate value={i18nKeys.FORM.ORG_ID_LABEL} />
            </label>
            <Input
              type="text"
              value={orgIdInput}
              placeholder={I18n.t(i18nKeys.FORM.ORG_ID_PLACEHOLDER)}
              onChange={(_, { value }) => setOrgIdInput(value)}
              disabled={isLoading}
            />
          </Form.Field>
          <div className={styles.formActions}>
            <Button primary type="submit" loading={isLoading} disabled={isLoading}>
              <Translate value={i18nKeys.FORM.LOAD_BUTTON} />
            </Button>
            {(data || loadError || validationError) && (
              <Button type="button" onClick={resetToStart} disabled={isLoading}>
                <Translate value={i18nKeys.FORM.CLEAR_BUTTON} />
              </Button>
            )}
          </div>
        </Form>
      </Segment>

      {/* Validation error: a bad id, shown plainly without the load-failed framing */}
      {validationError && (
        <Message negative>
          <p>{validationError}</p>
        </Message>
      )}

      {/* Load error */}
      {loadError && (
        <Message negative>
          <Message.Header>
            <Translate value={i18nKeys.ERRORS.LOAD_FAILED} />
          </Message.Header>
          <p>{loadError}</p>
          <p>
            <Translate value={i18nKeys.ERRORS.LOAD_FAILED_HINT} />
          </p>
        </Message>
      )}

      {/* Loading */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Loader active inline="centered" />
        </div>
      )}

      {/* Review */}
      {!isLoading && data && (
        <>
          {data.incomplete && (
            <Message warning>
              <Translate value={i18nKeys.ERRORS.LOAD_INCOMPLETE} />
            </Message>
          )}
          <OrgDetailsCard details={data.details} impact={data.impact} />
          <OrgAdminsList admins={data.admins} totalCount={data.adminCount} />
          <div className={styles.disableActions}>
            <Button negative onClick={() => setModalOpen(true)}>
              <Translate value={i18nKeys.DISABLE_BUTTON} />
            </Button>
          </div>
        </>
      )}

      <DisableConfirmationModal
        isOpen={modalOpen}
        org={data?.details ?? null}
        isDisabling={isDisabling}
        error={disableError}
        onConfirm={handleConfirmDisable}
        onCancel={() => {
          setModalOpen(false);
          setDisableError(null);
        }}
      />
    </div>
  );
};

export default DisableOrganization;
